// Story 11.5: Scroll-reveal primitive. One-shot GSAP + ScrollTrigger
// entrance tween that fires when a section crosses the viewport threshold
// (default `top 85%`). Honors `prefers-reduced-motion` via a static render
// that never imports gsap (Pattern 5 short-circuit). Replaces the 8+ inline
// `import('gsap').then(...)` + `gsap.context()` + `ScrollTrigger` blocks
// that used to live in individual landing-page sections.

import { useEffect, useRef, type HTMLAttributes, type JSX, type ReactNode, type Ref } from 'react';
import { loadGsapWithPlugin, prefersReducedMotion } from '../../../lib/gsap-setup.js';

// Item tagging — consumers opt into per-child stagger by adding either the
// class or the data-attribute to each direct child they want targeted. The
// default selector constrains to direct children (`:scope >`) so a consumer
// that tags cards doesn't leak the stagger to their grand-children. When a
// consumer needs to stagger nested elements (e.g. lines inside a <blockquote>
// wrapped in a <figure>), they can override `itemSelector` with any selector
// that is valid inside `gsap.context(scope)` — the selector runs rooted at
// the ScrollReveal container.
export const SCROLL_REVEAL_ITEM_CLASS = 'scroll-reveal-item';
export const SCROLL_REVEAL_ITEM_ATTR = 'data-scroll-reveal-item';
const DEFAULT_ITEM_SELECTOR = `:scope > .${SCROLL_REVEAL_ITEM_CLASS}, :scope > [${SCROLL_REVEAL_ITEM_ATTR}]`;

type ForwardedHtmlProps = Omit<HTMLAttributes<HTMLElement>, 'children'>;

export interface ScrollRevealProps extends ForwardedHtmlProps {
  children: ReactNode;
  // Semantic landmark preference. 'section' for page sections, 'div' otherwise.
  as?: 'section' | 'div';
  // Seconds between children reveals; 0 (default) targets the container itself.
  stagger?: number;
  // translateY start in px. Default 24.
  rise?: number;
  // Seconds per tween. Default 0.6.
  duration?: number;
  // GSAP ScrollTrigger start string (e.g. "top 85%"). Default "top 85%".
  threshold?: string;
  // One-shot by default (fires once per section, not scrubbed). Matches
  // UX-spec line 1153 "fires once per section, not scrubbed to scroll position".
  once?: boolean;
  // Override the `:scope >` direct-child item selector. Only read when
  // `stagger > 0`. Use when the stagger targets are nested deeper than the
  // direct children (e.g. lines inside a <blockquote> in a <figure>).
  itemSelector?: string;
  'data-testid'?: string;
}

export function ScrollReveal(props: ScrollRevealProps): JSX.Element {
  if (prefersReducedMotion) return <ScrollRevealStatic {...props} />;
  return <ScrollRevealMotion {...props} />;
}

function ScrollRevealStatic(props: ScrollRevealProps): JSX.Element {
  const {
    as = 'div',
    children,
    'data-testid': dataTestId,
    // Strip motion-only props so they don't leak to the DOM element.
    stagger: _stagger,
    rise: _rise,
    duration: _duration,
    threshold: _threshold,
    once: _once,
    itemSelector: _itemSelector,
    ...rest
  } = props;
  void _stagger;
  void _rise;
  void _duration;
  void _threshold;
  void _once;
  void _itemSelector;
  const Tag = as;
  return (
    <Tag {...rest} data-testid={dataTestId ?? 'scroll-reveal'} data-mode="static">
      {children}
    </Tag>
  );
}

function ScrollRevealMotion(props: ScrollRevealProps): JSX.Element {
  const {
    as = 'div',
    children,
    stagger = 0,
    rise = 24,
    duration = 0.6,
    threshold = 'top 85%',
    once = true,
    itemSelector,
    'data-testid': dataTestId,
    ...rest
  } = props;
  const resolvedItemSelector = itemSelector ?? DEFAULT_ITEM_SELECTOR;
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    let ctx: { revert: () => void } | undefined;
    void loadGsapWithPlugin(() => import('gsap/ScrollTrigger'), 'ScrollTrigger')
      .then(({ gsap }) => {
        if (cancelled) return;
        ctx = gsap.context(() => {
          const target: Element | NodeListOf<Element> =
            stagger > 0 ? el.querySelectorAll(resolvedItemSelector) : el;
          gsap.from(target, {
            y: rise,
            opacity: 0,
            duration,
            stagger: stagger > 0 ? stagger : 0,
            ease: 'power2.out',
            scrollTrigger: { trigger: el, start: threshold, once },
          });
        }, el);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      ctx?.revert();
    };
  }, [stagger, rise, duration, threshold, once, resolvedItemSelector]);

  const Tag = as;
  // `Tag` is a union of intrinsic elements — TypeScript can't narrow the ref
  // type across the union, so we cast. The runtime Element is always the
  // right subtype of HTMLElement for the chosen Tag.
  const refProp = ref as unknown as Ref<HTMLDivElement> & Ref<HTMLElement>;
  return (
    <Tag {...rest} ref={refProp} data-testid={dataTestId ?? 'scroll-reveal'} data-mode="motion">
      {children}
    </Tag>
  );
}
